import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Check,
  CornerDownRight,
  Filter,
  Link2,
  ListChecks,
  MessageSquare,
  MessagesSquare,
  Reply,
  Shield,
  Trash2,
  X,
} from 'lucide-react';
import type { CommentStatus, ListCommentsQuery } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { CommentThreadDialog } from '@/components/comments/comment-thread-dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import {
  commentsListQueryOptions,
  useBulkUpdateCommentStatus,
  useDeleteComment,
  useStaffReply,
  useUpdateCommentStatus,
} from '@/lib/comments';
import { hasPermission } from '@/lib/permissions';
import { useSession } from '@/lib/auth';

const STATUS_PILLS: { value: CommentStatus | undefined; label: string }[] = [
  { value: undefined, label: 'ทั้งหมด' },
  { value: 'pending', label: 'รออนุมัติ' },
  { value: 'approved', label: 'อนุมัติแล้ว' },
  { value: 'rejected', label: 'ปฏิเสธ' },
  { value: 'spam', label: 'สแปม' },
];

const STATUS_TINT: Record<CommentStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  spam: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const STATUS_LABEL: Record<CommentStatus, string> = {
  pending: 'รออนุมัติ',
  approved: 'อนุมัติ',
  rejected: 'ปฏิเสธ',
  spam: 'สแปม',
};

interface CommentsSearch {
  page?: number;
  perPage?: number;
  status?: CommentStatus;
  q?: string;
  postId?: string;
  ipAddress?: string;
  from?: string;
  to?: string;
}

interface DatePreset {
  label: string;
  compute: () => { from: string; to: string };
}

const DATE_PRESETS: DatePreset[] = [
  {
    label: '1 ชม. ล่าสุด',
    compute: () => {
      const end = new Date();
      const start = new Date(end.getTime() - 60 * 60 * 1000);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
  {
    label: 'วันนี้',
    compute: () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
  {
    label: '7 วันล่าสุด',
    compute: () => {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 7);
      return { from: start.toISOString(), to: end.toISOString() };
    },
  },
];

function shortRange(from: string | undefined, to: string | undefined): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('th-TH', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `ตั้งแต่ ${fmt(from)}`;
  if (to) return `ถึง ${fmt(to)}`;
  return '';
}

export const Route = createFileRoute('/_authenticated/comments/')({
  validateSearch: (search: Record<string, unknown>): CommentsSearch => ({
    page: typeof search.page === 'number' ? search.page : undefined,
    perPage: typeof search.perPage === 'number' ? search.perPage : undefined,
    status:
      search.status === 'pending' ||
      search.status === 'approved' ||
      search.status === 'rejected' ||
      search.status === 'spam'
        ? search.status
        : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    postId: typeof search.postId === 'string' ? search.postId : undefined,
    ipAddress: typeof search.ipAddress === 'string' ? search.ipAddress : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  component: CommentsPage,
});

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CommentsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const filter: Partial<ListCommentsQuery> = {
    page: search.page ?? 1,
    perPage: search.perPage ?? 50,
    status: search.status,
    q: search.q,
    postId: search.postId,
    ipAddress: search.ipAddress,
    from: search.from,
    to: search.to,
  };
  const query = useQuery(commentsListQueryOptions(filter));
  const update = useUpdateCommentStatus();
  const bulkUpdate = useBulkUpdateCommentStatus();
  const del = useDeleteComment();
  const reply = useStaffReply();
  // Title of the post being filtered to — taken from the first row of the
  // current page (every row in a postId-filtered list shares the same post).
  // Falls back to a short id slice while the query is loading.
  const activePostTitle = search.postId
    ? (query.data?.items[0]?.postTitle ??
      `post ${search.postId.slice(0, 4)}…${search.postId.slice(-4)}`)
    : null;

  const [searchInput, setSearchInput] = useState(filter.q ?? '');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [pendingDelete, setPendingDelete] = useState<{ id: string; body: string } | null>(null);
  const [replyTo, setReplyTo] = useState<{
    parentId: string;
    postId: string;
    postTitle: string;
    authorName: string;
    body: string;
  } | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [threadId, setThreadId] = useState<string | null>(null);

  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (filter.perPage ?? 50)));
  const currentPage = filter.page ?? 1;
  const allOnPageIds = items.map((c) => c.id);
  const allOnPageSelected = allOnPageIds.length > 0 && allOnPageIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;
  const canModerate = hasPermission(user, 'comment.moderate');
  const canDelete = hasPermission(user, 'comment.delete');
  const canReply = hasPermission(user, 'comment.reply_as_staff');
  const canReadAudit = hasPermission(user, 'audit.read');

  const setStatusFilter = (value: CommentStatus | undefined) => {
    setSelected(new Set());
    void navigate({ search: { ...search, status: value, page: 1 } });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSelected(new Set());
    void navigate({ search: { ...search, q: searchInput.trim() || undefined, page: 1 } });
  };

  const goPage = (page: number) => {
    setSelected(new Set());
    void navigate({ search: { ...search, page } });
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    setSelected((prev) => {
      if (allOnPageSelected) {
        const next = new Set(prev);
        for (const id of allOnPageIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of allOnPageIds) next.add(id);
      return next;
    });
  };

  const bulkSetStatus = (status: CommentStatus) => {
    if (selected.size === 0) return;
    bulkUpdate.mutate(
      { ids: Array.from(selected), status },
      { onSuccess: () => setSelected(new Set()) },
    );
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <MessageSquare className="h-6 w-6 text-slate-400" />
              จัดการคอมเมนต์
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {total > 0 ? `${total} รายการ` : 'ยังไม่มีคอมเมนต์'}
              {/* Active filter count — parity with /posts + /audit. */}
              {(() => {
                const n = [
                  search.status,
                  search.q,
                  search.postId,
                  search.ipAddress,
                  search.from,
                  search.to,
                ].filter(Boolean).length;
                return n > 0 ? (
                  <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    กรอง {n}
                  </span>
                ) : null;
              })()}
            </p>
          </div>
          {/* Share-link parity with /posts + /audit. Useful for
              moderators handing off a triage view ("look at this
              spam IP filter, I'm out for lunch"). */}
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                .writeText(window.location.href)
                .then(() => toast.success('คัดลอกลิงก์รวม filter แล้ว'))
                .catch(() => {
                  // Clipboard denied — silent.
                });
            }}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            title="คัดลอกลิงก์หน้านี้ (พร้อม filter)"
          >
            <Link2 className="h-3.5 w-3.5" />
            ลิงก์
          </button>
        </div>

        {/* Active post-filter chip — shown above the status pills so it
            sits at the top of the filter stack. Cleared via X. The
            entry point is the Filter icon in each comment row. */}
        {activePostTitle && (
          <div>
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <Filter className="h-3 w-3 shrink-0" />
              <span className="truncate">บน: {activePostTitle}</span>
              <button
                type="button"
                onClick={() => void navigate({ search: { ...search, postId: undefined, page: 1 } })}
                aria-label="ล้างตัวกรองโพสต์"
                className="ml-1 shrink-0 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}

        {/* Date range presets — scopes the list to a moderation window
            ("comments from the last hour" for spam triage, "today" for
            the daily review pass). Active chip below shows the resolved
            absolute range so the user can confirm what they're seeing. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-500">ช่วงเวลา:</span>
          {DATE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                const { from, to } = p.compute();
                void navigate({ search: { ...search, from, to, page: 1 } });
              }}
              className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              {p.label}
            </button>
          ))}
          {(search.from || search.to) && (
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-100 px-3 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              {shortRange(search.from, search.to)}
              <button
                type="button"
                onClick={() =>
                  void navigate({
                    search: { ...search, from: undefined, to: undefined, page: 1 },
                  })
                }
                aria-label="ล้างช่วงเวลา"
                className="ml-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>

        {/* IP filter chip — entry point is the per-row IP click below.
            Useful for spam-pattern detection (same IP, multiple
            comments, different "author names"). */}
        {search.ipAddress && (
          <div>
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-red-100 px-3 py-1 font-mono text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
              IP: {search.ipAddress}
              <button
                type="button"
                onClick={() =>
                  void navigate({ search: { ...search, ipAddress: undefined, page: 1 } })
                }
                aria-label="ล้างตัวกรอง IP"
                className="ml-1 shrink-0 rounded-full hover:bg-red-200 dark:hover:bg-red-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}

        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_PILLS.map((p) => {
            const active = search.status === p.value;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => setStatusFilter(p.value)}
                className={
                  active
                    ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                    : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                }
              >
                {p.label}
              </button>
            );
          })}
          {/* Master clear — wipes every dimension (status / postId /
              IP / q / date range) at once. Same gesture as /posts +
              /audit. Only shown when at least one filter is active. */}
          {(search.status ||
            search.postId ||
            search.ipAddress ||
            search.q ||
            search.from ||
            search.to) && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: {} });
              }}
              className="ml-1 text-xs text-slate-500 hover:underline"
              title="ล้างทุกตัวกรอง"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ค้นหาในเนื้อคอมเมนต์..."
            className="max-w-md"
          />
          <Button type="submit" variant="outline">
            ค้นหา
          </Button>
          {search.q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: { ...search, q: undefined, page: 1 } });
              }}
            >
              ล้าง
            </Button>
          )}
        </form>

        {/* Bulk bar */}
        {someSelected && canModerate && (
          <div className="bg-brand-50 dark:bg-brand-900/30 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
            <span className="text-sm font-medium">เลือก {selected.size} รายการ</span>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={bulkUpdate.isPending}
                onClick={() => bulkSetStatus('approved')}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                อนุมัติ
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={bulkUpdate.isPending}
                onClick={() => bulkSetStatus('rejected')}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                ปฏิเสธ
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={bulkUpdate.isPending}
                onClick={() => bulkSetStatus('spam')}
              >
                <Shield className="mr-1.5 h-3.5 w-3.5" />
                สแปม
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              disabled={bulkUpdate.isPending}
              className="ml-auto"
            >
              ล้างเลือก
            </Button>
          </div>
        )}

        {/* Results */}
        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <ListChecks className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ไม่มีคอมเมนต์ที่ตรงกัน</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                  <tr>
                    {canModerate && (
                      <th className="w-10 px-4 py-3 font-medium">
                        <input
                          type="checkbox"
                          aria-label="เลือกทั้งหน้า"
                          checked={allOnPageSelected}
                          onChange={toggleAllOnPage}
                          className="h-4 w-4"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 font-medium">ผู้เขียน</th>
                    <th className="px-4 py-3 font-medium">คอมเมนต์</th>
                    <th className="px-4 py-3 font-medium">สถานะ</th>
                    <th className="px-4 py-3 font-medium">เมื่อ</th>
                    <th className="w-32 px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {items.map((c) => {
                    const isSelected = selected.has(c.id);
                    return (
                      <tr
                        key={c.id}
                        className={
                          isSelected
                            ? 'bg-brand-50/50 dark:bg-brand-900/20'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                        }
                      >
                        {canModerate && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              aria-label={`เลือก ${c.authorName}`}
                              checked={isSelected}
                              onChange={() => toggleOne(c.id)}
                              className="h-4 w-4"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 dark:text-slate-50">
                            {c.authorName}
                            {/* "ฉัน" badge for comments authored by the
                                current user — typically own staff
                                replies (useStaffReply writes with
                                c.authorId = me.id). Helps moderators
                                spot their own contributions in a
                                shared queue. */}
                            {user && c.authorId === user.id && (
                              <span className="ml-1.5 rounded bg-slate-200 px-1 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                ฉัน
                              </span>
                            )}
                          </div>
                          {c.authorEmail && (
                            <a
                              href={`mailto:${c.authorEmail}`}
                              className="hover:text-brand-600 dark:hover:text-brand-400 block font-mono text-[11px] text-slate-400 hover:underline"
                              title="ส่งอีเมลถึงผู้เขียนคอมเมนต์"
                            >
                              {c.authorEmail}
                            </a>
                          )}
                          {/* IP click → filter all comments from this
                              address. Spam runs often come from one IP
                              with rotating display names; this turns
                              that gesture into one click. Hidden when
                              already filtering on this IP (no-op). */}
                          {c.ipAddress && search.ipAddress !== c.ipAddress && (
                            <button
                              type="button"
                              onClick={() =>
                                void navigate({
                                  search: { ...search, ipAddress: c.ipAddress!, page: 1 },
                                })
                              }
                              className="hover:text-brand-600 dark:hover:text-brand-400 mt-0.5 block font-mono text-[10px] text-slate-400 hover:underline"
                              title="กรองคอมเมนต์จาก IP นี้"
                            >
                              ip: {c.ipAddress}
                            </button>
                          )}
                        </td>
                        <td className="max-w-md px-4 py-3">
                          {/* Reply indicator — quick visual cue when this
                              comment is part of a thread. Moderators
                              often want to read the parent for context;
                              the existing "ดูเป็นบทสนทนา" icon opens
                              the full thread dialog. */}
                          {c.parentId && (
                            <div className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-slate-400">
                              <CornerDownRight className="h-2.5 w-2.5" />
                              ตอบกลับ
                            </div>
                          )}
                          <div className="line-clamp-3 text-slate-700 dark:text-slate-300">
                            {c.body}
                          </div>
                          <Link
                            to="/posts/$postId"
                            params={{ postId: c.postId }}
                            className="hover:text-brand-600 dark:hover:text-brand-400 mt-1 block truncate text-[11px] text-slate-500"
                          >
                            บน: {c.postTitle}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TINT[c.status]}`}
                          >
                            {STATUS_LABEL[c.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400">
                          {formatDateTime(c.createdAt)}
                          {/* Fresh-comment pill — parity with posts.
                              Helps moderators scan for today's incoming
                              activity without comparing timestamps. */}
                          {Date.now() - new Date(c.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
                            <div className="mt-0.5 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              ใหม่
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* Filter the list to just this post's comments —
                                handy when one article generates a flood and
                                the moderator wants to focus. No-op when the
                                filter is already on this post. */}
                            {search.postId !== c.postId && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  void navigate({
                                    search: { ...search, postId: c.postId, page: 1 },
                                  })
                                }
                                aria-label="กรองคอมเมนต์ของโพสต์นี้"
                                title="กรองคอมเมนต์ของโพสต์นี้"
                              >
                                <Filter className="h-4 w-4 text-slate-500" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setThreadId(c.id)}
                              aria-label="ดูเป็นบทสนทนา"
                              title="ดูเป็นบทสนทนา"
                            >
                              <MessagesSquare className="h-4 w-4 text-slate-500" />
                            </Button>
                            {canReply && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setReplyTo({
                                    parentId: c.id,
                                    postId: c.postId,
                                    postTitle: c.postTitle,
                                    authorName: c.authorName,
                                    body: c.body,
                                  });
                                  setReplyBody('');
                                }}
                                aria-label="ตอบกลับ"
                                title="ตอบกลับ"
                              >
                                <Reply className="h-4 w-4 text-slate-500" />
                              </Button>
                            )}
                            {canModerate && c.status !== 'approved' && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={update.isPending}
                                onClick={() => update.mutate({ id: c.id, status: 'approved' })}
                                aria-label="อนุมัติ"
                                title="อนุมัติ"
                              >
                                <Check className="h-4 w-4 text-emerald-600" />
                              </Button>
                            )}
                            {canModerate && c.status !== 'rejected' && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={update.isPending}
                                onClick={() => update.mutate({ id: c.id, status: 'rejected' })}
                                aria-label="ปฏิเสธ"
                                title="ปฏิเสธ"
                              >
                                <X className="h-4 w-4 text-slate-500" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={del.isPending}
                                onClick={() =>
                                  setPendingDelete({ id: c.id, body: c.body.slice(0, 80) })
                                }
                                aria-label="ลบ"
                                title="ลบถาวร"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                            {canReadAudit && (
                              <Link
                                to="/audit"
                                search={{ resourceType: 'comment', resourceId: c.id }}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                                title="ดู audit ของคอมเมนต์นี้"
                                aria-label="ดู audit"
                              >
                                <Activity className="h-4 w-4" />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">แสดง</span>
                <select
                  value={filter.perPage ?? 50}
                  onChange={(e) =>
                    void navigate({
                      search: { ...search, perPage: Number(e.target.value), page: 1 },
                    })
                  }
                  className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  aria-label="จำนวนรายการต่อหน้า"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-slate-500">รายการ</span>
              </div>
              {totalPages > 1 ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => goPage(currentPage - 1)}
                  >
                    ก่อนหน้า
                  </Button>
                  <span className="text-slate-500">
                    หน้า {currentPage} จาก {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => goPage(currentPage + 1)}
                  >
                    ถัดไป
                  </Button>
                </>
              ) : (
                <span className="text-slate-500">หน้า 1 จาก 1</span>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ลบคอมเมนต์ — กู้คืนไม่ได้"
        description={
          pendingDelete
            ? `จะลบคอมเมนต์ "${pendingDelete.body}${pendingDelete.body.length >= 80 ? '...' : ''}" ออกจากระบบถาวร`
            : undefined
        }
        confirmLabel="ลบถาวร"
        variant="destructive"
        onConfirm={() => {
          if (pendingDelete) {
            del.mutate(pendingDelete.id, { onSuccess: () => setPendingDelete(null) });
          }
        }}
        isLoading={del.isPending}
      />

      <CommentThreadDialog
        open={threadId !== null}
        onOpenChange={(open) => {
          if (!open) setThreadId(null);
        }}
        commentId={threadId}
      />

      <Dialog
        open={replyTo !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReplyTo(null);
            setReplyBody('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="h-5 w-5 text-slate-400" />
              ตอบกลับในนามทีมงาน
            </DialogTitle>
            <DialogDescription>
              คำตอบจะถูก auto-approve และโชว์เป็นทีมงาน บนโพสต์เดียวกับคอมเมนต์ต้นทาง
            </DialogDescription>
          </DialogHeader>
          {replyTo && (
            <div className="space-y-3 py-2">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-700 dark:bg-slate-800/50">
                <div className="mb-1 font-medium text-slate-700 dark:text-slate-200">
                  {replyTo.authorName}{' '}
                  <span className="font-normal text-slate-400">บน {replyTo.postTitle}</span>
                </div>
                <p className="line-clamp-3 text-slate-600 dark:text-slate-300">{replyTo.body}</p>
              </div>
              <Textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="พิมพ์คำตอบ..."
                rows={4}
                autoFocus
              />
              <p className="text-[11px] text-slate-400">
                สูงสุด 2000 ตัวอักษร · {replyBody.trim().length} / 2000
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setReplyTo(null);
                setReplyBody('');
              }}
              disabled={reply.isPending}
            >
              ยกเลิก
            </Button>
            <Button
              type="button"
              disabled={reply.isPending || replyBody.trim().length === 0}
              onClick={() => {
                if (!replyTo) return;
                reply.mutate(
                  {
                    postId: replyTo.postId,
                    parentId: replyTo.parentId,
                    body: replyBody.trim(),
                  },
                  {
                    onSuccess: () => {
                      setReplyTo(null);
                      setReplyBody('');
                    },
                  },
                );
              }}
            >
              {reply.isPending ? 'กำลังส่ง...' : 'ส่งคำตอบ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
